import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { GridRowParams } from '@material-ui/data-grid';
import { toJS } from 'mobx';
import { IconButton, ListItem, Typography } from '@material-ui/core';
import _ from 'lodash';
import { VMPaginatedGrid } from '../../components/main/VMPaginatedGrid';
import { Api } from '../../api';
import ActionsMenu from '../../components/other/ActionsMenu';
import { Configuration } from '../../types/Charger';
import { drawerStore } from '../../state/drawerStore';
import { usePaginatedProvider } from '../../state/paginatedProviderStore';
import { gridColumn } from '../../utils/gridUtils';
import credentialStore from '../../state/credentialStore';
import { FormInputTypes } from '../../utils/constants';

const readConfigurationInputs = [
  {
    label: 'Manufacturer / Model',
    name: 'manufacturerModel',
    type: FormInputTypes.disabledTextField,
  },
  { name: 'comment', type: FormInputTypes.textField },
  { name: 'settings', type: FormInputTypes.editableJsonView },
];

const OperationConfigsTable = observer(
  ({
    chargerProvider,
    operationConfigProvider,
    onClickCreateOperationConfig,
    onClickConfirmAdd,
    sortableFields,
  }: any) => {
    const getFilterByManufacturerAndModel = (manufacturer, model) => (charger) =>
      charger.model === model && charger.manufacturer === manufacturer;

    const getMapToOptions = (charger) => ({
      topRow: `${charger.ocppId}`,
      bottomRow: charger.id,
      optionName: charger.id,
      label: `${charger.manufacturer} / ${charger.model}`,
    });

    const getApplyOptions = (configuration) =>
      chargerProvider.data
        ?.filter(getFilterByManufacturerAndModel(configuration.manufacturer, configuration.model))
        .map(getMapToOptions);

    const getAssignedChargersOptions = (configuration) =>
      chargerProvider.data
        ?.filter(getFilterByManufacturerAndModel(configuration.manufacturer, configuration.model))
        ?.filter((charger) => configuration.assignedChargerIds.includes(charger.id))
        .map((charger) => ({ ...charger, locationName: `${charger.location?.name}` }))
        .map(getMapToOptions);

    const onClickConfirmApply = async (configuration: any, options: any) => {
      const configurationId = configuration.id;

      const applyOption = drawerStore.values.applyOption;
      const chargePointValues =
        applyOption === 'selected' ? drawerStore.values.chargePoints_filterList : options;

      const createAllChargePointPromises = chargePointValues
        .map((option) => ({
          configurationId,
          chargePointId: option.optionName,
        }))
        .map(async (chargePointConfig) => {
          try {
            await Api.charge.createChargePointConfiguration(chargePointConfig);
          } catch (error) {
            console.error(
              `Failed to create charge point configuration for ID: ${chargePointConfig.chargePointId}`,
              error,
            );
          }
        });

      await Promise.all(createAllChargePointPromises);
      operationConfigProvider.reloadResource();
    };

    const onClickDuplicateConfiguration = (configuration: Configuration) => {
      const selectedConfiguration = operationConfigProvider.data.find(
        (item) => item.id === configuration.id,
      );

      drawerStore.openDrawer({
        label: 'Duplicate operation config',
        inputs: readConfigurationInputs,
        clickCallback: () => onClickConfirmAdd(),
        buttonLabel: 'Create',
      });
      const initialValues = {
        manufacturerModel: `${selectedConfiguration.manufacturer} / ${selectedConfiguration.model}`,
        settings: selectedConfiguration.settings,
      };
      drawerStore.setValues(initialValues);
    };

    const onClickDeleteConfiguration = (configuration: Configuration) => {
      Api.charge
        .deleteoperationConfig(configuration.id)
        .then(operationConfigProvider.reloadResource);
    };

    const onApplyConfiguration = (configuration: Configuration) => {
      drawerStore.openDrawer({
        label: 'Apply operation config',
        inputs: [
          {
            label: 'revision no.',
            name: 'revision',
            type: FormInputTypes.disabledTextField,
            value: configuration.revision,
          },
          {
            id: 'abc',
            label: '',
            name: 'applyOption',
            type: FormInputTypes.radioButton,
            options: [
              { label: 'All', value: 'all' },
              { label: 'Selected', value: 'selected' },
            ],
          },
          {
            name: 'chargePoints',
            type: FormInputTypes.filterListSelect,
            filter: { label: 'Apply to', name: 'chargePoints' },
            options: getApplyOptions(configuration),
          },
        ],

        clickCallback: () => onClickConfirmApply(configuration, getApplyOptions(configuration)),
        buttonLabel: 'Apply configuration',
      });
      drawerStore.setValues({ applyOption: 'all' });
    };

    const operationConfigActions = (params: GridRowParams) => (
      <ActionsMenu>
        <ListItem button onClick={() => onClickDuplicateConfiguration(params.row as Configuration)}>
          <Typography style={{ width: '100%' }}>Duplicate</Typography>
        </ListItem>
        <ListItem button onClick={() => onApplyConfiguration(params.row as Configuration)}>
          <Typography style={{ width: '100%' }}>Apply</Typography>
        </ListItem>
        <ListItem button onClick={() => onClickDeleteConfiguration(params.row as Configuration)}>
          <Typography style={{ width: '100%' }}>Delete</Typography>
        </ListItem>
      </ActionsMenu>
    );

    const onClickChargersCount = (configuration) => {
      drawerStore.openDrawer({
        label: 'Assigned charge points',
        inputs: [
          {
            label: 'revision no.',
            name: 'revision',
            type: FormInputTypes.disabledTextField,
            value: configuration.revision,
          },
          {
            label: '',
            name: 'chargePoints',
            type: FormInputTypes.textLinkView,
            filter: { label: 'Apply to', name: 'chargePoints' },
            options: getAssignedChargersOptions(configuration),
          },
        ],
        clickCallback: undefined,
        buttonLabel: 'Close',
      });
      drawerStore.setValues({ revision: configuration.revision, applyOption: 'all' });
    };

    const getAllChargersColumns = () => [
      gridColumn('manufacturer', {
        flex: 2,
        headerName: 'Manufacturer',
        sortableFields,
        minWidth: 150,
        valueGetter: (params: GridRowParams) => `${params.row.manufacturer}`,
      }),
      gridColumn('model', {
        flex: 2,
        headerName: 'Model',
        sortableFields,
        minWidth: 150,
        valueGetter: (params: GridRowParams) => `${params.row.model}`,
      }),
      gridColumn('revision', {
        flex: 2,
        headerName: 'Revision',
        sortableFields,
        minWidth: 150,
        valueGetter: (params: GridRowParams) => `${params.row.revision}`,
      }),
      gridColumn('comment', {
        flex: 2,
        headerName: 'Comment',
        sortableFields,
        minWidth: 150,
        valueGetter: (params: GridRowParams) => `${params.row.comment}`,
      }),
      gridColumn('chargers', {
        flex: 1,
        headerName: 'Chargers',
        minWidth: 150,
        renderCell: (params: GridRowParams) =>
          params.row.assignedChargerIds.length === 0 ? (
            <Typography color="textSecondary">0</Typography>
          ) : (
            <IconButton
              onClick={() => onClickChargersCount(params.row)}
              style={{ padding: 0, justifyContent: 'center', alignItems: 'center' }}
            >
              <Typography color="primary">{params.row.assignedChargerIds.length}</Typography>
            </IconButton>
          ),
      }),
      gridColumn('createdAt', {
        flex: 2,
        headerName: 'Created At',
        sortableFields,
        minWidth: 150,
        valueGetter: (params: GridRowParams) => {
          const date = new Date(params.row.createdAt);
          const day = String(date.getDate()).padStart(2, '0');
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const year = date.getFullYear();
          return `${day}/${month}/${year}`;
        },
      }),
      gridColumn('', {
        width: 60,
        renderCell: (params: GridRowParams) => operationConfigActions(params),
        align: 'center',
        headerAlign: 'center',
      }),
    ];

    return (
      <div style={{ width: '100%' }}>
        <VMPaginatedGrid
          rows={operationConfigProvider.data}
          columns={getAllChargersColumns()}
          onClickAdd={
            credentialStore.isUserAdmin ? () => onClickCreateOperationConfig() : undefined
          }
          onPageOffsetChange={operationConfigProvider.onPageChange}
          onSortTypeChange={operationConfigProvider.setSort}
          label="Operation Configurations"
        />
      </div>
    );
  },
);

const OperationConfigs = observer(() => {
  const operationConfigProvider = usePaginatedProvider(undefined, undefined, false);
  const chargerProvider = usePaginatedProvider(undefined, undefined, false);
  const [sortableFields, setSortableFields] = useState([]);
  const operationConfigSearchText = drawerStore.values.operationConfigSearchText;
  const [createOperationConfigOpen, setCreateOperationConfigOpen] = useState(false);

  useEffect(() => {
    operationConfigProvider.setRequest((offset, sortBy, sortOrder) =>
      Api.charge.listoperationConfigs({ skip: offset, take: 20, sortBy, sortOrder }),
    );

    operationConfigProvider.setOnFulfilled((_data, _count, sortable) => {
      if (sortable) setSortableFields(sortable);
    });

    operationConfigProvider.reloadResource();

    chargerProvider.setRequest((offset, sortBy, sortOrder) =>
      Api.charge.listChargers({ skip: offset, sortBy, sortOrder }),
    );

    chargerProvider.setOnFulfilled((_data, _count, sortable) => {
      if (sortable) setSortableFields(sortable);
    });

    chargerProvider.reloadResource();
  }, [operationConfigProvider, chargerProvider]);

  const getChargerOptions = useCallback(() => {
    const data =
      (!operationConfigSearchText
        ? chargerProvider.data
        : chargerProvider.data?.filter((charger) =>
            charger.ocppId.toLowerCase().includes(operationConfigSearchText.toLowerCase()),
          )) || [];

    return data.map((charger) => {
      const label = `${charger.manufacturer} / ${charger.model}`;
      return {
        topRow: label,
        bottomRow: charger.ocppId,
        optionName: charger.id,
        label,
      };
    });
  }, [operationConfigSearchText]);

  const onClickConfirmAdd = useCallback(() => {
    const configuration = toJS(drawerStore.values);

    const { manufacturerModel, ...rest } = configuration;

    const [manufacturer, model] = manufacturerModel.split('/').map((part) => part.trim());

    const newConfiguration = {
      ...rest,
      manufacturer,
      model,
    };
    if (credentialStore.isUserAdmin) {
      Api.charge.createoperationConfig(newConfiguration).then((response) => {
        operationConfigProvider.reloadResource();
      });
    }
  }, []);

  const onReadConfigurationClicked = async () => {
    const chargePointId = drawerStore.values.chargePoint;

    if (chargePointId === undefined) {
      return;
    }

    drawerStore.openDrawer({
      label: 'Create operation config',
      inputs: [{ name: 'loading', type: FormInputTypes.loader }],
    });

    const configurationRequest = (await Api.charge.getConfigurationRequest(chargePointId)).data;
    const configuration = (
      await Api.charge.getConfigurationRequestDetail(
        chargePointId,
        configurationRequest.data.taskId,
      )
    ).data;

    const selectedCharger = getChargerOptions().find(
      (charger) => charger.optionName === chargePointId,
    );

    const [selectedManufacturer, selectedModel] = selectedCharger.label
      .split('/')
      .map((part) => part.trim());
    let initialValues = {
      manufacturerModel: `${selectedManufacturer} / ${selectedModel}`,
      settings: [],
    };

    if (configuration.data?.response) {
      initialValues = {
        ...initialValues,
        settings: configuration.data.response.configurationKeys,
      };
    }

    drawerStore.openDrawer({
      label: 'Create operation config',
      inputs: readConfigurationInputs,
      clickCallback: () => onClickConfirmAdd(),
      buttonLabel: 'Create',
    });
    drawerStore.setValues(initialValues);
  };

  const onClickCreateOperationConfig = useCallback(() => {
    setCreateOperationConfigOpen(true);
    drawerStore.setResetCallback(() => setCreateOperationConfigOpen(false));
    drawerStore.openDrawer({
      label: 'Create operation config',
      inputs: [],
    });
  }, []);

  const operationConfigInputs = useMemo(
    () => [
      {
        name: 'operationConfigSearchText',
        label: 'Search by OCPP ID',
        type: FormInputTypes.textField,
      },
      {
        name: 'chargePoint',
        label: 'Charge Point',
        type: FormInputTypes.multilineSelect,
        options: getChargerOptions(),
      },
      {
        name: 'readConfiguration',
        type: FormInputTypes.textButton,
        label: 'Read Configuration',
        onClick: () => onReadConfigurationClicked(),
      },
    ],
    [operationConfigSearchText, createOperationConfigOpen],
  );

  useEffect(() => {
    drawerStore.setInputs(operationConfigInputs);
    drawerStore.setValues({
      ...drawerStore.values,
      chargePoint: operationConfigInputs[1]?.options?.[0]?.optionName,
    });
  }, [getChargerOptions, chargerProvider.data, createOperationConfigOpen]);

  return (
    <OperationConfigsTable
      chargerProvider={chargerProvider}
      operationConfigProvider={operationConfigProvider}
      onClickCreateOperationConfig={onClickCreateOperationConfig}
      onClickConfirmAdd={onClickConfirmAdd}
      sortableFields={sortableFields}
    />
  );
});

export default OperationConfigs;
